import { fetchProjectGRActionItemsProjects } from '@gp/services/grActionItems';
import { sortByCharsPure, getSpecialListValues, toOptions, mappingToOptions } from '@gp/utils/common';
import {
  getActionItemStatusNameByText,
  renameOfProjectGroupOptions,
  getCascadeProjectDatas,
  getCascadeProjectValues,
} from '@gp/utils/project';
import { SortType } from '@gp/enums/common';
import { GROUP_KEY } from '@gp/constants/select';
import { STAGE_OPT } from '@gp/pages/Workspace/Product/components/GameOverview/utils/constant';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { IRecord } from '@gp/types/global';
import type {
  IProjectGRActionItemsFilterVO,
  IProjectGRActionItemsFilterConditionsDTO,
  IProjectGRActionItemsFilterConditionsVO,
  IProjectGRActionItemsFilterValuesConditionsVO,
  IProjectGRActionItemProjectDTO,
  IProjectGRActionItemProjectVO,
  IProjectGRActionItemsFetchParams,
} from '@gp/types/grActionItems';

const DEFAULT_STATUS = ['canceled', 'in progress', 'not started', 'on hold', 'others'];
const getStatusValues = (list?: string[]): string[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const result = list.filter((item) => DEFAULT_STATUS.includes(item?.toLowerCase()));
  return result.length > 0 ? result : list;
};

const DEFAULT_STAGES = ['live', 'development'];
const getStateValues = (list?: string[]): string[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const result = list.filter((item) => DEFAULT_STAGES.includes(item?.toLowerCase()));
  return result.length > 0 ? result : list;
};

export const formatFilterConditions = (
  filterConditions?: IProjectGRActionItemsFilterConditionsDTO,
): IProjectGRActionItemsFilterValuesConditionsVO => {
  const {
    action_item_gate_review: gateReviewConditions,
    action_item_owner: ownerConditions,
    action_item_status: statusConditions,
    stage: stageConditions,
    action_item_due: actionItemDue,
    pteam_bo_studio_project: pteamProject,
  } = filterConditions || {};

  const { pteam_list: pteamList = [] } = pteamProject || {};
  const { start_date: minDate, end_date: maxDate } = actionItemDue || {};

  const stageConditionsUpperCase = stageConditions?.map((item) => item?.toUpperCase());

  const gateReviewConditionsSorted = sortByCharsPure(gateReviewConditions);
  const ownerConditionsSorted = sortByCharsPure(ownerConditions);
  const statusConditionsSorted = sortByCharsPure(statusConditions);

  const sortedGateReviewOptions = toOptions(gateReviewConditionsSorted);
  const sortedOwnerOptions = toOptions(ownerConditionsSorted);
  const sortedStatusOptions = toOptions(statusConditionsSorted, getActionItemStatusNameByText);
  const sortedStageOptions = STAGE_OPT.filter((item) => {
    return stageConditionsUpperCase?.includes(item?.value?.toUpperCase());
  }).map((item) => ({ ...item, groupKey: GROUP_KEY, label: item?.text || '' }));

  const statusOptionsValues = sortedStatusOptions.map((item) => item?.value);
  const stageOptionsValues = sortedStageOptions.map((item) => item?.value);

  // project配置项及初始化全选
  const cascadeProjectoptions = getCascadeProjectDatas(pteamList, 'project_auth');

  return {
    values: {
      dueDate: {
        dateType: 'date',
        startDate: minDate || '',
        endDate: maxDate || '',
      },
      gate_review: gateReviewConditionsSorted,
      owner: ownerConditionsSorted,
      status: getStatusValues(statusOptionsValues),
      stage: getStateValues(stageOptionsValues),
      project_id: getCascadeProjectValues(cascadeProjectoptions, true),
    },
    conditions: {
      dueDate: {
        min_date: minDate || '',
        max_date: maxDate || '',
      },
      gate_review: sortedGateReviewOptions,
      owner: sortedOwnerOptions,
      status: sortedStatusOptions,
      stage: sortedStageOptions,
      project_id: cascadeProjectoptions || [],
    },
    defaultValues: {
      project_id: getCascadeProjectValues(cascadeProjectoptions, false),
    },
  };
};

export const formatFilterFetchParams = (
  payload?: IProjectGRActionItemsFilterVO,
  filterConditions?: IProjectGRActionItemsFilterConditionsVO,
): Omit<
IProjectGRActionItemsFetchParams,
'sort' | 'sort_type' | 'sec_sort' | 'sec_sort_type' | 'search_type' | 'search' | 'project_ids'
> => {
  const {
    dueDate,
    pteam,
    business_owner: businessOwner,
    studio,
    gate_review: gateReview,
    owner,
    status,
    stage,
    project_id,
  } = payload || {};
  const { startDate, endDate } = dueDate || {};

  const {
    pteam: pteamOptions,
    business_owner: boOptions,
    studio: studioOptions,
    gate_review: gateReviewOptions,
    owner: ownerOptions,
    status: statusOptions,
    stage: stageOptions,
    // project_id: cascadeProjectOptions,
  } = filterConditions || {};

  return {
    // Due Date开始日期
    start_date: startDate || '',
    // Due Date结束日期
    end_date: endDate || '',
    // Review
    gate_review: getSpecialListValues(gateReview, gateReviewOptions),
    // Owner
    owner: getSpecialListValues(owner, ownerOptions),
    // Status
    status: getSpecialListValues(status, statusOptions),
    // Stage
    stage: getSpecialListValues(stage, stageOptions),
    project_id,
  };
};

const SORT_MAPPINGS: IRecord<string> = {
  project: 'priority',
};
export const formatSortFetchParams = (
  sorts?: SortBy<IProjectGRActionItemProjectVO>[],
): Pick<IProjectGRActionItemsFetchParams, 'sort' | 'sort_type' | 'sec_sort' | 'sec_sort_type'> => {
  const result = {
    sort: '',
    sort_type: SortType.None,
    sec_sort: '',
    sec_sort_type: SortType.None,
  };
  if (!Array.isArray(sorts) || !sorts.length) return result;
  for (const sort of sorts) {
    const { by, order } = sort || {};
    if (!by || !order) continue;
    if (by === 'due_date') {
      result.sec_sort = by;
      result.sec_sort_type = order as SortType;
    } else {
      result.sort = SORT_MAPPINGS[by] ?? by;
      result.sort_type = order as SortType;
    }
  }
  return result;
};

export const getSearchList = async (
  payload?: { projectName?: string },
  params?: {
    sort: SortBy<IProjectGRActionItemProjectVO>[];
  },
): Promise<IProjectGRActionItemProjectDTO[]> => {
  const { projectName } = payload || {};
  const { sort } = params || {};
  const sortParams = formatSortFetchParams(sort);
  const res = await fetchProjectGRActionItemsProjects({
    start_date: '',
    end_date: '',
    gate_review: [],
    owner: [],
    status: [],
    stage: [],
    ...sortParams,
    search_type: 'dim',
    search: projectName || '',
    project_ids: [],
  });
  return res?.data || [];
};
